import { useState } from 'react'
import { MessageSquare, Send } from 'lucide-react'
import { fetchApi, OrzForgeApiError } from '../lib/api'
import type { ChatCompletionResponse, ChatMessage } from '../types'

const MODELS = [
  '@cf/meta/llama-3.1-8b-instruct',
  '@cf/mistral/mistral-7b-instruct-v0.1',
  '@cf/qwen/qwen1.5-14b-chat-awq',
]

export function AiPage() {
  const [model, setModel] = useState(MODELS[0])
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'system',
      content:
        'Eres OrzAI, un asistente útil ejecutándose en el edge de Cloudflare a través de OrzForge.',
    },
  ])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function send(e?: React.FormEvent) {
    e?.preventDefault()
    if (!input.trim()) return

    const userMsg: ChatMessage = { role: 'user', content: input }
    const nextMessages = [...messages, userMsg]
    setMessages(nextMessages)
    setInput('')
    setLoading(true)
    setError(null)

    try {
      const body = {
        model,
        messages: nextMessages.filter((m) => m.role !== 'system' || m.content),
        max_tokens: 512,
      }
      const res = (await fetchApi('/v1/ai/chat/completions', {
        method: 'POST',
        body: JSON.stringify(body),
      })) as ChatCompletionResponse

      const assistant = res.choices[0]?.message
      if (assistant) {
        setMessages([...nextMessages, assistant])
      }
    } catch (err) {
      setError(err instanceof OrzForgeApiError ? err.message : 'Error en OrzAI')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex h-[calc(100vh-6rem)] flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">OrzAI</h1>
          <p className="text-sm text-gray-400">Playground de modelos Workers AI</p>
        </div>
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="rounded-lg border border-white/10 bg-panel px-3 py-2 text-sm outline-none focus:border-brand-500"
        >
          {MODELS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </header>

      <div className="flex-1 overflow-auto rounded-xl border border-white/10 bg-panel p-4">
        {messages.filter((m) => m.role !== 'system').length === 0 && !loading && (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-gray-500">
            <MessageSquare className="h-10 w-10" />
            <p>Escribe un mensaje para empezar.</p>
          </div>
        )}
        <div className="space-y-4">
          {messages
            .filter((m) => m.role !== 'system')
            .map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-3xl rounded-2xl px-4 py-2.5 text-sm ${
                    m.role === 'user'
                      ? 'bg-brand-600 text-white'
                      : 'border border-white/10 bg-surface text-gray-200'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
          {loading && (
            <div className="flex justify-start">
              <div className="rounded-2xl border border-white/10 bg-surface px-4 py-2.5 text-sm text-gray-400">
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <form onSubmit={send} className="flex items-center gap-3">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe tu mensaje..."
          className="flex-1 rounded-lg border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-600 text-white shadow-lg shadow-brand-600/20 transition hover:bg-brand-500 disabled:opacity-60"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  )
}
